import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useMe } from "../hooks/useMe";
import { AvatarPicker } from "../components/AvatarPicker";
import { ConfirmButton } from "../components/ConfirmButton";

export function AccountPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useMe();

  /* ---- Display name ---- */
  const [displayName, setDisplayName] = useState(me?.displayName ?? "");
  const [profileSaved, setProfileSaved] = useState(false);
  const profileMutation = useMutation({
    mutationFn: () =>
      api.auth.updateMe({ displayName: displayName.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    },
  });

  /* ---- Avatar ---- */
  const avatarMutation = useMutation({
    mutationFn: (avatar: string | null) => api.auth.updateMe({ avatar }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["avatars"] });
    },
  });
  const currentAvatar = me?.avatar ?? null;

  /* ---- Change password ---- */
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSaved, setPwSaved] = useState(false);
  const passwordMutation = useMutation({
    mutationFn: () =>
      api.auth.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwError(null);
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 3000);
    },
    onError: (err) => setPwError((err as Error).message),
  });

  function onChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    setPwSaved(false);
    if (newPassword !== confirmPassword) {
      setPwError("New password and confirmation do not match");
      return;
    }
    if (newPassword.length < 8) {
      setPwError("New password must be at least 8 characters");
      return;
    }
    passwordMutation.mutate();
  }

  /* ---- Log out everywhere ---- */
  async function logoutEverywhere() {
    await api.auth.logoutAll();
    qc.setQueryData(["me"], null);
    navigate("/login");
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-2xl font-semibold">Account settings</h1>

      {/* Profile */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
        <h2 className="mb-1 text-xl font-semibold">Profile</h2>
        <p className="mb-4 text-sm text-slate-600">
          Signed in as <strong>@{me?.username}</strong>.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            profileMutation.mutate();
          }}
          className="flex items-end gap-3"
        >
          <label className="flex-1 text-sm">
            <span className="text-slate-600">Display name</span>
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              placeholder="Your name"
            />
          </label>
          <button
            type="submit"
            disabled={profileMutation.isPending}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {profileMutation.isPending ? "Saving…" : "Save"}
          </button>
        </form>
        {profileSaved && (
          <p className="mt-2 text-sm text-green-700">Display name saved.</p>
        )}
      </section>

      {/* Avatar */}
      {me && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
          <h2 className="mb-1 text-xl font-semibold">Avatar</h2>
          <p className="mb-4 text-sm text-slate-600">
            Pick a look. “Default” is the unique pattern generated from your
            account.
          </p>
          <AvatarPicker
            userId={me.id}
            name={me.username}
            value={currentAvatar}
            disabled={avatarMutation.isPending}
            onSelect={(v) => avatarMutation.mutate(v)}
          />
        </section>
      )}

      {/* Change password */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
        <h2 className="mb-1 text-xl font-semibold">Change password</h2>
        <p className="mb-4 text-sm text-slate-600">
          Changing your password logs out all your other sessions.
        </p>
        <form onSubmit={onChangePassword} className="max-w-sm space-y-3">
          <label className="block text-sm">
            <span className="text-slate-600">Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              maxLength={128}
              required
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </label>
          {pwError && (
            <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
              {pwError}
            </div>
          )}
          {pwSaved && (
            <div className="rounded bg-green-50 px-3 py-2 text-sm text-green-700">
              Password changed. Other sessions have been logged out.
            </div>
          )}
          <button
            type="submit"
            disabled={passwordMutation.isPending}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {passwordMutation.isPending ? "Changing…" : "Change password"}
          </button>
        </form>
      </section>

      {/* Sessions */}
      <section className="rounded-lg border border-slate-200 bg-[var(--surface)] p-4">
        <h2 className="mb-1 text-xl font-semibold">Sessions</h2>
        <p className="mb-4 text-sm text-slate-600">
          Sign out of Kandoo on every device, including this one.
        </p>
        <ConfirmButton
          onConfirm={logoutEverywhere}
          title="Log out everywhere"
          className="rounded border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          confirmClassName="rounded bg-red-600 px-4 py-2 text-sm text-white"
          confirmLabel="Log out of all sessions?"
        >
          Log out everywhere
        </ConfirmButton>
      </section>
    </div>
  );
}
